import React from 'react';
import '../assets/FamilyCard.css';

/**
 * FamilyCard Component
 * 
 * Displays a single family card with improved visual design and WCAG AA compliance.
 * 
 * Design Specifications:
 * - WCAG AA compliant color contrast ratios
 * - Typography hierarchy with distinguishable font sizes
 * - Responsive layout for mobile, tablet, and desktop
 * - Interactive hover states and transitions
 * 
 * @param {Object} props - Component props
 * @param {Object} props.family - Family data object
 * @param {number} props.family.familyNumber - Unique family identifier
 * @param {string} props.family.familyCode - Display code for the family
 * @param {string} props.family.parentName - Parent/guardian name
 * @param {string} props.family.parentPhone - Parent phone number
 * @param {string} [props.family.parentEmail] - Parent email (optional)
 * @param {number} props.family.totalTutees - Number of tutees in family
 * @param {number} props.family.totalActiveSubjects - Number of active subjects
 * @param {number} props.family.monthlyRevenue - Monthly revenue amount
 * @param {Array} props.family.tutees - Array of tutee objects
 * @param {Function} props.onClick - Handler for family card click
 * @param {Function} props.onTuteeClick - Handler for tutee chip click
 */
const FamilyCard = React.memo(({ family, onClick, onTuteeClick }) => {
  const {
    familyNumber,
    familyCode,
    parentName,
    parentPhone,
    parentEmail,
    totalTutees,
    totalActiveSubjects,
    monthlyRevenue,
    tutees = []
  } = family;

  /**
   * Format currency with proper decimal places and separators
   * @param {number} amount - Amount to format
   * @returns {string} Formatted currency string
   */
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '$0.00';
    }
    return `$${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  /**
   * Handle family card click
   */
  const handleCardClick = () => {
    if (onClick) {
      onClick(familyNumber);
    }
  };

  /**
   * Handle tutee chip click with event propagation stop
   * @param {Event} e - Click event
   * @param {string} tuteeId - Tutee ID
   */
  const handleTuteeChipClick = (e, tuteeId) => {
    e.stopPropagation();
    if (onTuteeClick) {
      onTuteeClick(e, tuteeId);
    }
  };

  return (
    <div 
      className="family-card" 
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      aria-label={`Family ${familyCode} - ${parentName}, ${totalTutees} tutees, ${formatCurrency(monthlyRevenue)} monthly revenue`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      {/* Header Section */}
      <div className="family-card-header">
        <div className="family-code">{familyCode}</div>
        <div className="family-revenue-badge" title="Average monthly revenue over the past 6 months">
          {formatCurrency(monthlyRevenue)}
          <div className="revenue-label">Avg/month (6-mo)</div>
        </div>
      </div>

      {/* Parent Information Section */}
      <div className="family-parent-info">
        <div className="parent-name">{parentName || 'Name not provided'}</div>
        <div className="parent-contact">
          <div className="parent-phone">
            <span className="contact-label">Phone:</span> {parentPhone || 'Not provided'}
          </div>
          <div className="parent-email">
            <span className="contact-label">Email:</span> {parentEmail || 'Not provided'}
          </div>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="family-stats-grid">
        <div className="stat-item">
          <div className="stat-label">Tutees</div>
          <div className="stat-value">{totalTutees}</div>
        </div>
        <div className="stat-item">
          <div className="stat-label">Subjects</div>
          <div className="stat-value">{totalActiveSubjects}</div>
        </div>
      </div>

      {/* Tutees Section */}
      <div className="family-tutees-section">
        <div className="tutees-label">Children:</div>
        <div className="tutees-chips">
          {tutees.map((tutee) => (
            <div
              key={tutee._id}
              className={`tutee-chip ${tutee.isActive === false ? 'inactive' : ''}`}
              onClick={(e) => handleTuteeChipClick(e, tutee._id)}
              role="button"
              tabIndex={0}
              aria-label={`View ${tutee.name}, ${tutee.level}, ${tutee.activeSubjects} active subjects${tutee.isActive === false ? ' (Inactive)' : ''}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleTuteeChipClick(e, tutee._id);
                }
              }}
            >
              <div className="tutee-name">
                {tutee.name}
                {tutee.isActive === false && <span className="inactive-badge">Inactive</span>}
              </div>
              <div className="tutee-level">{tutee.level}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer Section */}
      <div className="family-card-footer">
        <button 
          className="view-details-btn"
          onClick={handleCardClick}
          aria-label={`View details for family ${familyCode}`}
        >
          View Details →
        </button>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Returns true if props are equal (skip re-render), false if different (re-render)
  
  // Compare family object properties
  if (prevProps.family.familyNumber !== nextProps.family.familyNumber) return false;
  if (prevProps.family.familyCode !== nextProps.family.familyCode) return false;
  if (prevProps.family.parentName !== nextProps.family.parentName) return false;
  if (prevProps.family.parentPhone !== nextProps.family.parentPhone) return false;
  if (prevProps.family.parentEmail !== nextProps.family.parentEmail) return false;
  if (prevProps.family.totalTutees !== nextProps.family.totalTutees) return false;
  if (prevProps.family.totalActiveSubjects !== nextProps.family.totalActiveSubjects) return false;
  if (prevProps.family.monthlyRevenue !== nextProps.family.monthlyRevenue) return false;
  
  // Compare tutees array length
  const prevTutees = prevProps.family.tutees || [];
  const nextTutees = nextProps.family.tutees || [];
  if (prevTutees.length !== nextTutees.length) return false;
  
  // Compare each tutee
  for (let i = 0; i < prevTutees.length; i++) {
    if (prevTutees[i]._id !== nextTutees[i]._id) return false;
    if (prevTutees[i].name !== nextTutees[i].name) return false;
    if (prevTutees[i].level !== nextTutees[i].level) return false;
    if (prevTutees[i].activeSubjects !== nextTutees[i].activeSubjects) return false;
    if (prevTutees[i].isActive !== nextTutees[i].isActive) return false;
  }
  
  // Function references are compared by reference, not by value
  // If onClick or onTuteeClick change, we should re-render
  if (prevProps.onClick !== nextProps.onClick) return false;
  if (prevProps.onTuteeClick !== nextProps.onTuteeClick) return false;
  
  // All props are equal, skip re-render
  return true;
});

export default FamilyCard;
